import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { WebSocketProvider } from './context/WebSocketContext';
import { EncryptionProvider } from './context/EncryptionContext';
import { ThemeProvider } from './context/ThemeContext';
import { Toaster } from 'sonner';
import './App.css';

// Pages
import Welcome from './pages/Welcome';
import Register from './pages/Register';
import Login from './pages/Login';
import LinkDevice from './pages/LinkDevice';
import ChatLayout from './pages/ChatLayout';
import Devices from './pages/Devices';
import Settings from './pages/Settings';
import AdminPanel from './pages/AdminPanel';
import NotificationSettings from './pages/NotificationSettings';
import EditProfile from './pages/EditProfile';
import AccountSwitcher from './pages/AccountSwitcher';
import VideoCall from './pages/VideoCall';
import EncryptionSettings from './pages/EncryptionSettings';
import PrivacySettings from './pages/PrivacySettings';
import ContactProfile from './pages/ContactProfile';
import CallsPage from './pages/CallsPage';
import UpdatesPage from './pages/UpdatesPage';

// Components
import IncomingCallModal from './components/IncomingCallModal';

const NotificationHandler = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  useEffect(() => {
    const setupNotifications = async () => {
      try {
        // Request browser notification permission for both web and native
        if ('Notification' in window && Notification.permission === 'default') {
          Notification.requestPermission();
        }
        
        // Register service worker for notification clicks
        if ('serviceWorker' in navigator) {
          navigator.serviceWorker.register('/service-worker.js').then((reg) => {
            console.log('[SW] Registered:', reg.scope);
          }).catch((err) => {
            console.log('[SW] Registration failed:', err);
          });
          
          // Listen for service worker notification click messages (PWA)
          navigator.serviceWorker.addEventListener('message', (event) => {
            if (event.data?.type === 'NOTIFICATION_CLICK' && event.data?.chatId) {
              navigate(`/chat/${event.data.chatId}`);
            }
          });
        }
        
        if (!window.Capacitor?.isNativePlatform()) return;
        const { FirebaseMessaging } = await import('@capacitor-firebase/messaging');
        
        // Create notification channel with sound + high importance for Android
        try {
          await FirebaseMessaging.createChannel({
            id: 'chatbuzzz_messages',
            name: 'Messages',
            description: 'Chat message notifications',
            importance: 5, // MAX importance for heads-up
            sound: 'default',
            vibration: true,
            lights: true,
            visibility: 1 // PUBLIC
          });
          // Create a separate channel for calls with full-screen intent
          await FirebaseMessaging.createChannel({
            id: 'chatbuzzz_calls',
            name: 'Incoming Calls',
            description: 'Voice and video call notifications',
            importance: 5, // MAX importance
            sound: 'default',
            vibration: true,
            lights: true,
            visibility: 1
          });
        } catch {}
        
        // Request all permissions at once (camera, mic, notifications)
        try {
          const permissionsToRequest = [];
          if (navigator.mediaDevices) {
            // Request camera + microphone access
            try {
              const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
              stream.getTracks().forEach(t => t.stop()); // Release immediately
            } catch {}
          }
        } catch {}
        
        // Handle foreground notifications - play sound + show local notification
        FirebaseMessaging.addListener('notificationReceived', (notification) => {
          const chatId = notification?.notification?.data?.chat_id;
          // Don't suppress - we still want sound. The notificationManager handles suppression for current chat
          try {
            const { playNotificationSound } = require('./utils/notificationSounds');
            playNotificationSound('default');
          } catch {}
        });
        
        // Handle notification tap when app is in background/killed
        FirebaseMessaging.addListener('notificationActionPerformed', (event) => {
          // Persistent debug log for notification click debugging
          const debugLog = JSON.stringify(event, null, 2);
          const existing = localStorage.getItem('notif_debug_log') || '';
          localStorage.setItem('notif_debug_log', existing + '\n---' + new Date().toISOString() + '---\n' + debugLog);
          console.log('[Notification] Action performed:', debugLog);
          
          // Try all possible data paths (varies by Capacitor plugin version and Android OS)
          const chatId = event?.notification?.data?.chat_id 
            || event?.notification?.data?.chatId
            || event?.data?.chat_id
            || event?.data?.chatId
            || event?.notification?.extra?.chat_id
            || event?.notification?.click_action_data?.chat_id
            || event?.notification?.tag;  // FCM tag also carries chat_id
          console.log('[Notification] Extracted chatId:', chatId);
          localStorage.setItem('notif_debug_chatId', chatId || 'null');
          
          if (chatId) {
            // Store in both storage types for maximum reliability
            localStorage.setItem('pending_nav_chat', chatId);
            sessionStorage.setItem('pending_nav_chat', chatId);
            // Try navigate immediately if user is available
            if (user) {
              navigate(`/chat/${chatId}`);
              localStorage.removeItem('pending_nav_chat');
              sessionStorage.removeItem('pending_nav_chat');
            }
            // Also dispatch event for the polling listener
            window.dispatchEvent(new CustomEvent('notification-nav', { detail: { chatId } }));
          }
        });
        
        // Check if app was opened via notification (cold start)
        try {
          const launchNotification = await FirebaseMessaging.getDeliveredNotifications();
          if (launchNotification?.notifications?.length > 0) {
            const notif = launchNotification.notifications[0];
            const chatId = notif?.data?.chat_id || notif?.data?.chatId || notif?.tag;
            console.log('[Notification] Cold start chatId:', chatId);
            if (chatId) {
              localStorage.setItem('pending_nav_chat', chatId);
              sessionStorage.setItem('pending_nav_chat', chatId);
              if (user) {
                navigate(`/chat/${chatId}`);
                // Clear the delivered notifications
                await FirebaseMessaging.removeAllDeliveredNotifications();
              }
            }
          }
        } catch {}
      } catch {}
    };
    setupNotifications();
  }, [navigate, user]);
  
  // Navigate to pending chat after login
  useEffect(() => {
    if (user) {
      const pendingChat = sessionStorage.getItem('pending_nav_chat') || localStorage.getItem('pending_nav_chat');
      const pendingTime = localStorage.getItem('pending_nav_time');
      
      // Only navigate if the pending chat was set recently (within 60 seconds)
      if (pendingChat) {
        const isRecent = !pendingTime || (Date.now() - parseInt(pendingTime, 10)) < 60000;
        if (isRecent) {
          console.log('[App] Navigating to pending chat:', pendingChat);
          sessionStorage.removeItem('pending_nav_chat');
          localStorage.removeItem('pending_nav_chat');
          localStorage.removeItem('pending_nav_time');
          navigate(`/chat/${pendingChat}`);
        } else {
          // Clear stale pending navigation
          sessionStorage.removeItem('pending_nav_chat');
          localStorage.removeItem('pending_nav_chat');
          localStorage.removeItem('pending_nav_time');
        }
      }
    }
  }, [user, navigate]);

  // Listen for window focus AND custom events to check for pending navigation
  useEffect(() => {
    const checkPendingNav = () => {
      const pendingChat = localStorage.getItem('pending_nav_chat') || sessionStorage.getItem('pending_nav_chat');
      if (pendingChat && user) {
        localStorage.removeItem('pending_nav_chat');
        sessionStorage.removeItem('pending_nav_chat');
        navigate(`/chat/${pendingChat}`);
      }
    };

    // Listen for custom notification-nav event (fires immediately on notification click)
    const handleNotificationNav = (event) => {
      const chatId = event.detail?.chatId;
      if (chatId && user) {
        localStorage.removeItem('pending_nav_chat');
        sessionStorage.removeItem('pending_nav_chat');
        navigate(`/chat/${chatId}`);
      }
    };

    // Poll for pending navigation every 500ms as reliable fallback
    const pollInterval = setInterval(checkPendingNav, 500);

    window.addEventListener('focus', checkPendingNav);
    window.addEventListener('notification-nav', handleNotificationNav);
    return () => {
      clearInterval(pollInterval);
      window.removeEventListener('focus', checkPendingNav);
      window.removeEventListener('notification-nav', handleNotificationNav);
    };
  }, [user, navigate]);

  // Version check - prompt user to update if a newer version is available
  useEffect(() => {
    if (!window.Capacitor?.isNativePlatform()) return;
    const APP_VERSION = '1.3.3'; // Must match backend current_version when APK is built

    // Compare semver: returns true if serverVersion is newer than appVersion
    const isNewer = (serverVer, appVer) => {
      const s = serverVer.split('.').map(Number);
      const a = appVer.split('.').map(Number);
      for (let i = 0; i < 3; i++) {
        if ((s[i] || 0) > (a[i] || 0)) return true;
        if ((s[i] || 0) < (a[i] || 0)) return false;
      }
      return false;
    };

    const checkVersion = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/app/version`);
        const data = await res.json();

        if (data.current_version && isNewer(data.current_version, APP_VERSION)) {
          console.log(`[Version] App: ${APP_VERSION}, Server: ${data.current_version}`);
          const updateUrl = data.update_url || 'https://www.chatbuzzz.com/apk/ChatBuzzz.apk';

          if (data.force_update) {
            // Block the app completely until they update
            const confirmed = window.confirm(
              `Update Required!\n\n` +
              `Your version: v${APP_VERSION}\n` +
              `Latest version: v${data.current_version}\n\n` +
              `You must update to continue using ChatBuzzz.\n\n` +
              `Click OK to download the new APK.`
            );
            if (confirmed && updateUrl) {
              window.open(updateUrl, '_blank');
            }
            // Keep checking and blocking
            setTimeout(checkVersion, 5000);
          } else {
            // Optional update - just notify once
            const confirmed = window.confirm(
              `Update Available!\n\n` +
              `A new version (v${data.current_version}) is available.\n\n` +
              `Click OK to download the update.`
            );
            if (confirmed && updateUrl) {
              window.open(updateUrl, '_blank');
            }
          }
        }
      } catch (e) {
        console.log('[Version] Check failed:', e);
      }
    };
    
    // Check on mount and every 5 minutes
    checkVersion();
    const interval = setInterval(checkVersion, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);
  
  return null;
};

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background-DEFAULT flex items-center justify-center">
        <div className="text-text-secondary">Loading...</div>
      </div>
    );
  }

  return user ? children : <Navigate to="/" replace />;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background-DEFAULT flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-3 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
          <span className="text-text-secondary text-sm">Loading ChatBuzzz...</span>
        </div>
      </div>
    );
  }

  return !user ? children : <Navigate to="/chats" replace />;
};

function AppContent() {
  return (
    <>
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<PublicRoute><Welcome /></PublicRoute>} />
        <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
        <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
        <Route path="/link-device" element={<PublicRoute><LinkDevice /></PublicRoute>} />

        {/* Protected Routes */}
        <Route path="/chats" element={<ProtectedRoute><ChatLayout /></ProtectedRoute>} />
        <Route path="/calls" element={<ProtectedRoute><CallsPage /></ProtectedRoute>} />
        <Route path="/updates" element={<ProtectedRoute><UpdatesPage /></ProtectedRoute>} />
        <Route path="/chat/:chatId" element={<ProtectedRoute><ChatLayout /></ProtectedRoute>} />
        <Route path="/devices" element={<ProtectedRoute><Devices /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
        <Route path="/settings/notifications" element={<ProtectedRoute><NotificationSettings /></ProtectedRoute>} />
        <Route path="/settings/profile" element={<ProtectedRoute><EditProfile /></ProtectedRoute>} />
        <Route path="/settings/accounts" element={<ProtectedRoute><AccountSwitcher /></ProtectedRoute>} />
        <Route path="/settings/encryption" element={<ProtectedRoute><EncryptionSettings /></ProtectedRoute>} />
        <Route path="/settings/privacy" element={<ProtectedRoute><PrivacySettings /></ProtectedRoute>} />
        <Route path="/call/:callId" element={<ProtectedRoute><VideoCall /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute><AdminPanel /></ProtectedRoute>} />
        <Route path="/contact/:userId" element={<ProtectedRoute><ContactProfile /></ProtectedRoute>} />
        <Route path="/contact/:userId/:chatId" element={<ProtectedRoute><ContactProfile /></ProtectedRoute>} />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      <Toaster 
        position="top-center"
        toastOptions={{
          style: {
            borderRadius: '0.75rem',
            padding: '0.75rem 1rem',
            fontSize: '0.875rem',
            border: 'none'
          },
          classNames: {
            success: '',
            error: ''
          }
        }}
        theme="light"
        richColors={false}
        style={{}}
      />
      <style>{`
        [data-sonner-toast][data-type="success"] { background: #075E54 !important; color: #fff !important; }
        [data-sonner-toast][data-type="error"] { background: #DC3545 !important; color: #fff !important; }
        [data-sonner-toast]:not([data-type]) { background: #075E54 !important; color: #fff !important; }
      `}</style>
      
      {/* Incoming Call Modal - shown globally */}
      <IncomingCallModal />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ThemeProvider>
          <AuthProvider>
            <NotificationHandler />
            <WebSocketProvider>
              <EncryptionProvider>
                <AppContent />
              </EncryptionProvider>
            </WebSocketProvider>
          </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;